import { Loader } from '@openx/components-core/lib/Loader/Loader';
import type { OptionsType } from '@openx/types/options';
import type { SelectRateFormState } from '@openx/types/targeting/selectRate';
import { noop } from '@openx/utils/noop';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useTargetingContext } from '../../../utils/context/TargetingContext';
import type { TargetingItemsProps } from '../../types';
import { FiltersTitle } from '../FiltersTitle/FiltersTitle';
import {
  TargetingDropdownSection,
  type TargetingDropdownSectionProps,
} from '../ReadOnlyTargetingDropdown/TargetingDropdownSection';
import { SelectedItems } from '../SelectedItems/SelectedItems';

interface SelectRateItemsProps extends TargetingItemsProps<SelectRateFormState> {
  title: string;
  dataTest?: string;
  optionsFetch: typeof OptionsType.VIEWABILITY | typeof OptionsType.VIEW_THROUGH_RATE;
}

export const FormPreviewItems = ({
  targetingParams,
  readonly = true,
  onChange = noop,
  title,
  optionsFetch,
  dataTest,
  onOpenDrawerClick,
  onRemove,
  isDisabled,
  optionsCount,
  disabledReasonMessage,
}: SelectRateItemsProps & TargetingDropdownSectionProps) => {
  const { t } = useTranslation();
  const { useSelectRateOptionsFetch } = useTargetingContext();
  const { isSelectRateLoading, selectRate: options } = useSelectRateOptionsFetch(optionsFetch);

  const { val } = targetingParams;

  const handleChange = useCallback(() => {
    onChange(state => ({ ...state, val: '' }));
  }, [onChange]);

  if (isSelectRateLoading) {
    return <Loader />;
  }

  if (!val) {
    return null;
  }

  return (
    <TargetingDropdownSection
      name={t(title)}
      hideButtons={!readonly}
      isEmpty={false}
      onOpenDrawerClick={onOpenDrawerClick}
      onRemove={onRemove}
      isDisabled={isDisabled}
      hasOptionsSelected={!!optionsCount && optionsCount > 0}
    >
      <div data-test="sub-section">
        <FiltersTitle
          onClear={handleChange}
          title={t(title)}
          readonly={readonly}
          disabledReasonMessage={disabledReasonMessage}
        />
        <SelectedItems
          dataTest={dataTest}
          isAddIcon
          selectedItems={[val]}
          readonly={readonly}
          handleChange={handleChange}
          options={options}
        />
      </div>
    </TargetingDropdownSection>
  );
};
